import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Shield, 
    AlertTriangle, 
    Clock, 
    Users, 
    ChevronRight,
    ArrowUpDown,
    Filter,
    RefreshCw,
    Building2,
    CheckCircle,
    XCircle,
    Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { portfolioAPI } from '../../lib/api';


// Risk level badge component
const RiskBadge = ({ level }) => {
    const styles = {
        high: 'bg-red-500/10 text-red-400 border-red-500/20',
        elevated: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
        normal: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    
    const labels = {
        high: 'High Risk',
        elevated: 'Elevated',
        normal: 'Normal',
    };
    
    return (
        <span className={`px-2 py-1 text-xs rounded border ${styles[level] || styles.normal}`}>
            {labels[level] || level}
        </span>
    );
};

// Risk indicator dot
const RiskDot = ({ level }) => {
    const colors = {
        high: 'bg-red-500',
        elevated: 'bg-amber-500',
        normal: 'bg-emerald-500',
    };
    return <div className={`w-2 h-2 rounded-full ${colors[level] || colors.normal}`}></div>;
};

export default function PortfolioRiskDashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [clients, setClients] = useState([]);
    const [sortBy, setSortBy] = useState('risk_score');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            // Shared axios client: a 401 here now clears the session and redirects to /login
            // instead of failing silently the way the raw fetch did.
            const [summaryRes, clientsRes] = await Promise.all([
                portfolioAPI.summary(),
                portfolioAPI.clients(sortBy, sortOrder),
            ]);
            setSummary(summaryRes.data);
            setClients(clientsRes.data.clients || []);
        } catch (err) {
            console.error('Error fetching portfolio data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [sortBy, sortOrder]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const filteredClients = clients.filter(client => 
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.cvr?.includes(searchTerm)
    );

    if (loading && !summary) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-muted-foreground">Loading portfolio data...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Header */}
            <div className="border-b border-border bg-background/95 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-muted border border-border rounded-lg flex items-center justify-center">
                                <Shield className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <h1 className="font-medium text-foreground">Portfolio Risk Dashboard</h1>
                                <p className="text-xs text-muted-foreground">Client portfolio overview</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={fetchData}
                                className="border-border text-muted-foreground hover:text-foreground"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Refresh
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
                    <div className="bg-card border border-border rounded-xl p-4">
                        <div className="text-2xl font-semibold text-foreground">{summary?.total_clients || 0}</div>
                        <div className="text-xs text-muted-foreground mt-1">Total Clients</div>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4">
                        <div className="text-2xl font-semibold text-red-400">{summary?.high_risk || 0}</div>
                        <div className="text-xs text-muted-foreground mt-1">High Risk</div>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4">
                        <div className="text-2xl font-semibold text-amber-400">{summary?.elevated_risk || 0}</div>
                        <div className="text-xs text-muted-foreground mt-1">Elevated Risk</div>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4">
                        <div className="text-2xl font-semibold text-emerald-400">{summary?.normal_risk || 0}</div>
                        <div className="text-xs text-muted-foreground mt-1">Normal</div>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4">
                        <div className="text-2xl font-semibold text-foreground">{summary?.open_exceptions || 0}</div>
                        <div className="text-xs text-muted-foreground mt-1">Open Exceptions</div>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4">
                        <div className="text-2xl font-semibold text-foreground">{summary?.clients_near_vat_deadline || 0}</div>
                        <div className="text-xs text-muted-foreground mt-1">Near VAT Deadline</div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex items-center gap-4 mb-6">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search clients by name or CVR..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground"
                        />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Sort:</span>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSort('risk_score')}
                            className={`text-xs ${sortBy === 'risk_score' ? 'text-foreground' : 'text-muted-foreground'}`}
                        >
                            Risk Score
                            <ArrowUpDown className="w-3 h-3 ml-1" />
                        </Button>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSort('days_to_vat_deadline')}
                            className={`text-xs ${sortBy === 'days_to_vat_deadline' ? 'text-foreground' : 'text-muted-foreground'}`}
                        >
                            VAT Deadline
                            <ArrowUpDown className="w-3 h-3 ml-1" />
                        </Button>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSort('exception_count')}
                            className={`text-xs ${sortBy === 'exception_count' ? 'text-foreground' : 'text-muted-foreground'}`}
                        >
                            Exceptions
                            <ArrowUpDown className="w-3 h-3 ml-1" />
                        </Button>
                    </div>
                </div>

                {/* Client List */}
                {clients.length === 0 ? (
                    <div className="bg-card border border-border rounded-xl p-12 text-center">
                        <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-foreground mb-2">Risk scoring isn&rsquo;t available yet</h3>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                            Nothing here is computed from your clients&rsquo; data yet. This screen will
                            stay empty until risk scoring is built — it will never show sample companies.
                            Your real clients and their documents are under{' '}
                            <strong>Companies</strong> and <strong>Overview</strong>.
                        </p>
                        <Button variant="outline" onClick={() => navigate('/app/companies')}>
                            Go to Companies
                        </Button>
                    </div>
                ) : (
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-border">
                                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-6 py-4">Client</th>
                                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-4">Risk Level</th>
                                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-4">Exceptions</th>
                                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-4">VAT Deadline</th>
                                    <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-4">Assigned To</th>
                                    <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-6 py-4"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {filteredClients.map((client) => (
                                    <tr 
                                        key={client.id} 
                                        className="hover:bg-muted/40 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/app/portfolio/client/${client.id}`)}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <RiskDot level={client.risk_level} />
                                                <div>
                                                    <div className="text-sm font-medium text-foreground">{client.name}</div>
                                                    <div className="text-xs text-muted-foreground">CVR: {client.cvr}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <RiskBadge level={client.risk_level} />
                                        </td>
                                        <td className="px-4 py-4">
                                            {client.open_exceptions > 0 ? (
                                                <span className="text-amber-400 text-sm">{client.open_exceptions} open</span>
                                            ) : (
                                                <span className="text-muted-foreground text-sm">None</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className={`text-sm ${client.days_to_vat_deadline <= 14 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                                                {client.days_to_vat_deadline} days
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="text-sm text-muted-foreground">{client.assigned_staff}</div>
                                            <div className="text-xs text-muted-foreground">{client.staff_role}</div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <ChevronRight className="w-4 h-4 text-muted-foreground inline" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
